/** One merged timeline for a record: system, human and approval events - P15 item 6.
 *
 * Pure, so the merge is tested without a screen. Four sources, each already on the page's reads:
 *
 * - **The ledger's acts about the record** (`GET /activity?record_id`): the chain's own sentences,
 *   each with its sequence. An `approval.*` entry is an approval event; one made by a person is a
 *   human event; anything else (the engine pricing a figure, the scheduler anchoring) is a system
 *   event.
 * - **The record's work** (`GET /records/{id}/work`): notes, flags and tasks, which are people's acts
 *   and have no ledger entry.
 * - **The full approval's sign-offs**, for the roles that cannot read the ledger's acts: the same
 *   decision, with its ledger sequence, so a reader of any role sees who approved and when.
 * - **The revision chain**: when each revision was recorded.
 *
 * **Deduplicated by ledger sequence.** A decision is a ledger entry *and* a sign-off; the same
 * sequence is one event, and the ledger's wording wins because it is the chain's.
 *
 * **Nothing a person wrote is copied in.** A note's text, a flag's reason and a task's title are
 * free text that may name a person; the timeline says that a note was added, not what it said (the
 * same line `approval_lists` rule 4 and the sign-off card draw).
 *
 * Actors are labels, never ids on screen as such: a person is "You", a name the page already holds,
 * or "Member a1b2" - the first four characters of an identifier, which names no one (rule 4).
 */

import type { ActivityRow } from "../api/activity";
import type { Activity } from "../api/queries";
import type { Work } from "../api/work";

export type EventType = "system" | "human" | "approval";

export const EVENT_TYPES: readonly { key: EventType; label: string }[] = [
  { key: "system", label: "System" },
  { key: "human", label: "People" },
  { key: "approval", label: "Approvals" },
];

export interface HistoryActor {
  /** Stable across events, so a chip filters one actor. */
  key: string;
  label: string;
}

export interface HistoryEvent {
  key: string;
  /** An ISO instant. */
  at: string;
  type: EventType;
  text: string;
  actor: HistoryActor;
  /** The ledger entry that holds it, when one does. */
  seq: number | null;
}

const SYSTEM: HistoryActor = { key: "system", label: "System" };

function capitalised(word: string): string {
  return word ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : word;
}

export function personActor(id: string | null | undefined, meId: string | undefined, name?: string | null): HistoryActor {
  if (!id) return { key: "person:unknown", label: "A member" };
  if (meId && id === meId) return { key: "me", label: "You" };
  if (name) return { key: `person:${id}`, label: name };
  return { key: `person:${id}`, label: "A colleague" };
}

interface Inputs {
  activity: Pick<Activity, "revisions">;
  work: Work | null | undefined;
  /** The ledger's acts about the record, or `undefined` when this role cannot read them. */
  acts: readonly ActivityRow[] | undefined;
  meId: string | undefined;
  /** A name for a person, where the reader may be given one (the Admin's roster). */
  nameOf?: (userId: string | null | undefined) => string | undefined;
}

export function historyOf({ activity, work, acts, meId, nameOf }: Inputs): HistoryEvent[] {
  const events = new Map<string, HistoryEvent>();
  const seqName = new Map<number, string>();
  for (const entry of work?.standing?.signoffs ?? []) {
    if (entry.ledger_seq !== null && entry.decided_by_name) seqName.set(entry.ledger_seq, entry.decided_by_name);
  }

  for (const row of acts ?? []) {
    const approval = row.type.startsWith("approval.");
    const byPerson = row.actor_kind === "user";
    events.set(`seq:${String(row.seq)}`, {
      key: `seq:${String(row.seq)}`,
      at: row.recorded_at,
      type: approval ? "approval" : byPerson ? "human" : "system",
      text: row.sentence,
      actor: byPerson
        ? personActor(row.actor_id, meId, nameOf?.(row.actor_id) ?? seqName.get(row.seq))
        : { key: row.actor_kind, label: capitalised(row.actor_kind) },
      seq: row.seq,
    });
  }

  for (const entry of work?.standing?.signoffs ?? []) {
    const key = entry.ledger_seq === null ? `signoff:${entry.decided_at}` : `seq:${String(entry.ledger_seq)}`;
    if (events.has(key)) continue;
    const verb =
      entry.decision === "approve" ? "approved" : entry.decision === "reject" ? "rejected" : "asked for a change on";
    events.set(key, {
      key,
      at: entry.decided_at,
      type: "approval",
      text: `${entry.decided_by_name ?? "A member"} ${verb} ${entry.stage_name ? `stage ${entry.stage_name}` : "a stage"}`,
      actor: entry.decided_by_name
        ? { key: `name:${entry.decided_by_name}`, label: entry.decided_by_name }
        : personActor(null, meId),
      seq: entry.ledger_seq,
    });
  }

  for (const note of work?.notes ?? []) {
    events.set(`note:${note.id}`, {
      key: `note:${note.id}`,
      at: note.created_at,
      type: "human",
      text: note.record_version ? `Note added on revision ${String(note.record_version)}` : "Note added",
      actor: personActor(note.author_id, meId, nameOf?.(note.author_id)),
      seq: null,
    });
  }
  for (const flag of work?.flags ?? []) {
    events.set(`flag:${flag.id}`, {
      key: `flag:${flag.id}`,
      at: flag.created_at,
      type: "human",
      text: `${capitalised(flag.severity)} flag raised${flag.state === "resolved" ? ", since resolved" : ""}`,
      actor: personActor(flag.raised_by, meId, nameOf?.(flag.raised_by)),
      seq: null,
    });
  }
  for (const task of work?.tasks ?? []) {
    events.set(`task:${task.id}`, {
      key: `task:${task.id}`,
      at: task.created_at,
      type: "human",
      text: "Task created",
      actor: personActor(task.created_by, meId, nameOf?.(task.created_by)),
      seq: null,
    });
  }

  activity.revisions.forEach((revision, at) => {
    events.set(`revision:${revision.activity_record_id}`, {
      key: `revision:${revision.activity_record_id}`,
      at: revision.created_at,
      type: "system",
      text: `Revision ${String(at + 1)} recorded`,
      actor: SYSTEM,
      seq: null,
    });
  });

  return [...events.values()].sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
}

/** The actors present in these events, once each, in the order they first appear. */
export function actorsOf(events: readonly HistoryEvent[]): HistoryActor[] {
  const seen = new Map<string, HistoryActor>();
  for (const event of events) if (!seen.has(event.actor.key)) seen.set(event.actor.key, event.actor);
  return [...seen.values()];
}

export function filtered(
  events: readonly HistoryEvent[],
  types: ReadonlySet<EventType>,
  actors: ReadonlySet<string>,
): HistoryEvent[] {
  return events.filter(
    (event) => (types.size === 0 || types.has(event.type)) && (actors.size === 0 || actors.has(event.actor.key)),
  );
}
